import React from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import NoDataPlaceholder from "app/shared/NoDataPlaceholder";
import { DsokManagementListToolbar } from "app/pages/customers/dsok/components/DsokManagementListToolbar";
import {
  deleteAccessSystemZone,
  getAccessSystemZones,
} from "app/services/api/accessSystemZones";
import { ZONES_MANAGEMENT_BASE_PATH } from "./zonesConstants";
import ZoneListItem from "./components/ZoneListItem";

const ZONES_I18N = "pages.dsokDetails.accessSystem.zones";
const ZONES_SEARCH_PAGE = "AccessSystemZones";

function zoneIdFromApi(z) {
  const id = z?._id ?? z?.id;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

function normalizeZoneForList(z) {
  const id = zoneIdFromApi(z);
  if (!id) return null;
  const devices = Array.isArray(z.devices) ? z.devices : [];
  return {
    id,
    name: z.name ?? "",
    description: z.description ?? "",
    isActive: z.isActive !== false,
    devices,
  };
}

function filterZoneRows(rows, queryRaw) {
  const q = String(queryRaw ?? "")
    .trim()
    .toLowerCase();
  if (!q) return rows;
  return (rows || []).filter((row) => {
    const name = String(row.name ?? "").toLowerCase();
    const desc = String(row.description ?? "").toLowerCase();
    return name.includes(q) || desc.includes(q);
  });
}

const AccessSystemZonesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();

  const [rows, setRows] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);

  const loadZones = React.useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const raw = await getAccessSystemZones();
      const list = Array.isArray(raw) ? raw : [];
      const mapped = list
        .map(normalizeZoneForList)
        .filter(Boolean);
      setRows(mapped);
    } catch (_e) {
      setRows([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadZones();
  }, [loadZones]);

  const resolveStatusLabel = React.useCallback(
    (isActive) =>
      isActive
        ? t(`${ZONES_I18N}.status.active`, { defaultValue: "Active" })
        : t(`${ZONES_I18N}.status.inactive`, { defaultValue: "Inactive" }),
    [t],
  );

  const filteredRows = React.useMemo(
    () => filterZoneRows(rows, searchQuery),
    [rows, searchQuery],
  );

  const listTitle = t("sidebar.menuItem.accessSystemZones", {
    defaultValue: "Zones",
  });

  const searchPlaceholder = t(`${ZONES_I18N}.list.searchPlaceholder`, {
    defaultValue: "Search by name or description",
  });

  const addTooltip = t(`${ZONES_I18N}.list.addNewRecord`, {
    defaultValue: "Create zone",
  });

  const loadErrorMessage = t(`${ZONES_I18N}.list.loadError`, {
    defaultValue: "Could not load zones.",
  });

  const deleteConfirmTitle = t(`${ZONES_I18N}.list.deleteConfirmTitle`, {
    defaultValue: "Delete zone?",
  });

  const handleDelete = React.useCallback(
    (item) => {
      const name =
        item.name != null && String(item.name).trim() !== ""
          ? String(item.name).trim()
          : item.id;
      showDialog({
        variant: "confirm",
        title: deleteConfirmTitle,
        content: (
          <Typography variant="body2">
            {t(`${ZONES_I18N}.list.deleteConfirmBody`, {
              name,
              defaultValue: `Delete zone "${name}"? This cannot be undone.`,
            })}
          </Typography>
        ),
        onYes: async () => {
          hideDialog();
          try {
            await deleteAccessSystemZone(item.id);
            setRows((prev) => (prev || []).filter((r) => r.id !== item.id));
            enqueueSnackbar(
              t("notifications.savedSuccessfully", {
                defaultValue: "Saved successfully",
              }),
              { variant: "success" },
            );
          } catch (_e) {
            enqueueSnackbar(
              t(`${ZONES_I18N}.list.deleteFailed`, {
                defaultValue: "Delete failed.",
              }),
              { variant: "error" },
            );
          }
        },
        onNo: () => hideDialog(),
      });
    },
    [deleteConfirmTitle, enqueueSnackbar, hideDialog, showDialog, t],
  );

  const renderMainContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (loadError) {
      return (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          <Alert severity="error">{loadErrorMessage}</Alert>
        </Box>
      );
    }

    if (!rows.length) {
      return (
        <JumboListNoDataPlaceholder>
          <NoDataPlaceholder />
        </JumboListNoDataPlaceholder>
      );
    }

    if (!filteredRows.length) {
      return (
        <JumboListNoDataPlaceholder>
          <NoDataPlaceholder />
        </JumboListNoDataPlaceholder>
      );
    }

    return (
      <>
        {filteredRows.map((row) => (
          <ZoneListItem
            key={row.id}
            item={row}
            devicesCount={(row.devices || []).length}
            resolveStatusLabel={resolveStatusLabel}
            onEdit={(id) =>
              navigate(`${ZONES_MANAGEMENT_BASE_PATH}/${encodeURIComponent(id)}`)
            }
            onDelete={handleDelete}
          />
        ))}
      </>
    );
  };

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: "none" }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <MuiLink
          component={Link}
          to="/customer/dsok"
          color="inherit"
          underline="hover"
        >
          {t("pages.pos.breadcrumbCustomer")}
        </MuiLink>
        <Link to="/customer/dsok/access-system/tickets-management">
          {t("sidebar.menuItem.accessSystem")}
        </Link>
        <Typography color="text.primary">{listTitle}</Typography>
      </Breadcrumbs>

      <Typography variant="h2" mb={3}>
        {listTitle}
      </Typography>

      <DsokManagementListToolbar
        searchPage={ZONES_SEARCH_PAGE}
        onSearchChange={setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        addTooltip={addTooltip}
        onAddClick={() => navigate(`${ZONES_MANAGEMENT_BASE_PATH}/new`)}
      />

      {renderMainContent()}
    </Box>
  );
};

export default AccessSystemZonesPage;
